import React, { RefObject, useMemo } from 'react';

const methodFactory = <T>(): [
  (key: string) => undefined | RefObject<T>,
  (key: string) => undefined | RefObject<T>,
] => {
  const map = new Map<string, RefObject<T>>();
  const setRef = (key: string): RefObject<T> => {
    // Reuse the existing ref object for this key so that ref.current is never
    // momentarily null between React's render phase (where setRef is called)
    // and its commit phase (where ref.current is attached to the DOM). Creating
    // a new createRef() on every render would overwrite the Map entry with a
    // null-current ref, which the ResizeObserver callback could read before
    // the commit phase sets it — causing a visibility: hidden flash in
    // components like Toggle that gate on optionWidths.some(w => w > 0).
    const existing = map.get(key);
    if (existing) return existing;
    const ref = React.createRef<T>();
    map.set(key, ref);
    return ref;
  };
  const getRef = (key: string): RefObject<T> | undefined => {
    const ref = map.get(key);
    return ref;
  };
  return [setRef, getRef];
};

export const useDynamicRefs = <T>(): [
  (key: string) => undefined | RefObject<T>,
  (key: string) => undefined | RefObject<T>,
] => {
  const refMethods = useMemo(() => methodFactory<T>(), []);
  return refMethods;
};
